import { useCallback, useSyncExternalStore } from 'react';

/**
 * "Show original colors", remembered per sender (ADR 0081).
 *
 * In a dark app every HTML message is shown dark -- the sender's own dark
 * design, or an adaptation of their light one. A reader who would rather see
 * a particular sender the way they sent it can say so once. That is an
 * APPEARANCE preference on this device, not a grant: it loads nothing, trusts
 * nothing and decides nothing about what a message may do, so it lives in
 * localStorage rather than beside the server-side remote-content rules, which
 * are grants and must follow the account and be auditable. Forgetting it
 * (private window, cleared storage) costs one click.
 *
 * Keyed on the From address, lowercased. From is forgeable, and here that is
 * harmless: the worst a forger gets is to be shown in their own colours.
 */
export const ORIGINAL_COLORS_KEY = 'ols-mail.original-colors';

/** A bound on what is kept, oldest dropped first. */
const MAX_SENDERS = 500;

function readSenders(): readonly string[] {
  try {
    const raw = localStorage.getItem(ORIGINAL_COLORS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

let senders: readonly string[] = typeof window === 'undefined' ? [] : readSenders();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === ORIGINAL_COLORS_KEY) {
      senders = readSenders();
      emit();
    }
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): readonly string[] {
  return senders;
}

function normalize(address: string): string {
  return address.trim().toLowerCase();
}

/** Re-reads storage; for tests. */
export function reloadOriginalColors(): void {
  senders = readSenders();
  emit();
}

export function setOriginalColorsFor(address: string, on: boolean): void {
  const key = normalize(address);
  if (!key) return;
  const rest = senders.filter((s) => s !== key);
  const next = on ? [...rest, key].slice(-MAX_SENDERS) : rest;
  senders = next;
  try {
    if (next.length === 0) localStorage.removeItem(ORIGINAL_COLORS_KEY);
    else localStorage.setItem(ORIGINAL_COLORS_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked: remembered for this page only */
  }
  emit();
}

/** Whether this sender's messages are always shown in their original colours, and a setter. */
export function useOriginalColorsFor(address: string | undefined): [boolean, (on: boolean) => void] {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  const key = address ? normalize(address) : '';
  const set = useCallback((on: boolean) => setOriginalColorsFor(key, on), [key]);
  return [key !== '' && list.includes(key), set];
}
